import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import { ArrowLeftIcon, BuildingOffice2Icon, EyeIcon, MegaphoneIcon, GlobeAltIcon, ShoppingBagIcon } from "@heroicons/react/24/outline";
import { ProgressSpinner } from "primereact/progressspinner";

const BusinessView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [record, setRecord] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get(`/business/admin/${id}`);
                if (response?.data?.success) {
                    setRecord(response.data.data);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) {
        return (
            <div className="unlist-margin flex justify-center items-center" style={{ minHeight: "300px" }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="unlist-margin">
            <div className="back-button mb-2" onClick={() => navigate("/admin/business")}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>

            <Card>
                <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                    <h2 className="m-0">View Business</h2>
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            label="View Public Page"
                            icon={<GlobeAltIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            outlined
                            severity="secondary"
                            onClick={() => window.open(`https://sagarsamaj.in/business/${id}`, "_blank")}
                        />
                        <Button
                            label={`Viewers (${record?.viewCount ?? 0})`}
                            icon={<EyeIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            outlined
                            onClick={() => navigate(`/admin/business/viewers/${id}`)}
                        />
                        <Button
                            label="Create Advertisement"
                            icon={<MegaphoneIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            severity="help"
                            outlined
                            onClick={() => navigate("/admin/advertisement/add", {
                                state: {
                                    prefill: {
                                        company_name: record?.name || "",
                                        mobile_number: record?.mobileNo || "",
                                        whatsapp_number: record?.whatsappNo || "",
                                        email: record?.email || "",
                                        website_link: record?.link || "",
                                        userid: record?.userid || "",
                                        ownerName: record?.ownerName || "",
                                        imageUrl: record?.logoUrl || "",
                                        short_description: record?.description && record.description.length <= 30 ? record.description : "",
                                        long_description: record?.description ? record.description : "",
                                    }
                                }
                            })}
                        />
                        <Button label="Edit" onClick={() => navigate(`/admin/business/edit/${id}`)} />
                    </div>
                </div>

                {record && (
                    <div className="grid grid-cols-12 gap-4">

                        {/* Logo + name */}
                        <div className="col-span-12 flex items-center gap-4">
                            {record.logoUrl ? (
                                <img src={record.logoUrl} alt={record.name} style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "10px", border: "1px solid #e5e7eb" }} />
                            ) : (
                                <div style={{ width: "80px", height: "80px", borderRadius: "10px", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <BuildingOffice2Icon style={{ width: "36px", height: "36px", color: "#d1d5db" }} />
                                </div>
                            )}
                            <div>
                                <h3 className="m-0">{record.name}</h3>
                                <p className="m-0 text-gray-500">{record.nameGujarati}</p>
                                <Tag className="mt-1" value={record.isactive ? "Active" : "Inactive"} severity={record.isactive ? "success" : "danger"} />
                            </div>
                        </div>

                        <div className="lg:col-span-6 col-span-12"><strong>Owner:</strong> {record.ownerName || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Business Type:</strong> {record.businessType || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Mobile No:</strong> {record.mobileNo || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>WhatsApp No:</strong> {record.whatsappNo || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Email:</strong> {record.email || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>GST No:</strong> {record.gstNo || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Establishment Year:</strong> {record.establishmentYear || "-"}</div>
                        <div className="lg:col-span-6 col-span-12">
                            <strong>Website / Link:</strong>{" "}
                            {record.link
                                ? <a href={record.link} target="_blank" rel="noreferrer" style={{ color: "#264164" }}>{record.link}</a>
                                : "-"}
                        </div>
                        <div className="col-span-12"><strong>Address:</strong> {record.address || "-"}</div>
                        <div className="col-span-12"><strong>Description:</strong> {record.description || "-"}</div>
                        <div className="col-span-12"><strong>Vision & Mission:</strong> {record.visionmission || "-"}</div>
                        <div className="lg:col-span-6 col-span-12">
                            <strong>Created Date:</strong> {formatDateTime(record.createddate)}
                        </div>

                        {/* Directors */}
                        {record.directors?.length > 0 && (
                            <div className="col-span-12">
                                <strong>Directors:</strong>
                                <div className="overflow-x-auto mt-2">
                                    <table className="w-full" style={{ borderCollapse: "collapse", minWidth: "500px" }}>
                                        <thead>
                                            <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                                                <th className="p-2 border-b">Image</th>
                                                <th className="p-2 border-b">Name</th>
                                                <th className="p-2 border-b">Position</th>
                                                <th className="p-2 border-b">Contact</th>
                                                <th className="p-2 border-b">Email</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {record.directors.map((d: any, i: number) => (
                                                <tr key={i}>
                                                    <td className="p-2 border-b">
                                                        {d.imageUrl
                                                            ? <img src={d.imageUrl} alt={d.name} style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "50%" }} />
                                                            : "-"}
                                                    </td>
                                                    <td className="p-2 border-b">{d.name || "-"}</td>
                                                    <td className="p-2 border-b">{d.position || "-"}</td>
                                                    <td className="p-2 border-b">{d.contactNo || "-"}</td>
                                                    <td className="p-2 border-b">{d.email || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Certificates */}
                        {record.certificateImageUrls?.length > 0 && (
                            <div className="col-span-12">
                                <strong>Certificates:</strong>
                                <div className="flex gap-3 flex-wrap mt-2">
                                    {record.certificateImageUrls.map((url: string, i: number) => (
                                        <a key={i} href={url} target="_blank" rel="noreferrer">
                                            <img src={url} alt="certificate" style={{ width: "110px", height: "110px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Products added under this business */}
                        {record.products?.length > 0 && (
                            <div className="col-span-12">
                                <strong className="flex items-center gap-2">
                                    <ShoppingBagIcon style={{ width: "18px", height: "18px" }} />
                                    Products ({record.products.length})
                                </strong>
                                <div className="grid grid-cols-12 gap-3 mt-2">
                                    {record.products.map((p: any) => (
                                        <div
                                            key={p.id}
                                            className="col-span-6 sm:col-span-4 lg:col-span-3 cursor-pointer"
                                            style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}
                                            onClick={() => navigate(`/admin/products/view/${p.id}`)}
                                        >
                                            {p.imageUrl ? (
                                                <img src={p.imageUrl} alt={p.name} style={{ width: "100%", height: "100px", objectFit: "cover" }} />
                                            ) : (
                                                <div style={{ width: "100%", height: "100px", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <ShoppingBagIcon style={{ width: "28px", height: "28px", color: "#d1d5db" }} />
                                                </div>
                                            )}
                                            <div style={{ padding: "8px 10px" }}>
                                                <p className="m-0 text-sm font-medium" style={{ color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</p>
                                                <p className="m-0 text-xs" style={{ color: "#264164", fontWeight: 700 }}>₹{p.price}</p>
                                                <Tag className="mt-1" value={p.isactive ? "Active" : "Inactive"} severity={p.isactive ? "success" : "danger"} style={{ fontSize: "10px" }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                )}
            </Card>
        </div>
    );
};

export default BusinessView;
